/**
 * Injeta shopify-forms-custom.css dentro do shadow root do app Shopify Forms.
 *
 * O app monta o formulário em <shopify-forms-embed> com shadow DOM, que isola o
 * conteúdo do CSS do tema. O host é criado em runtime e o momento disso depende do
 * gatilho do formulário (delay, scroll, exit intent), então quem manda aqui é um
 * MutationObserver que vive o tempo da página, sem prazo pra desistir.
 *
 * A URL do CSS vem do data-stylesheet da própria tag <script> (ver
 * snippets/shopify-forms-styles.liquid), pra o CSS continuar sendo um asset normal
 * do tema em vez de uma string dentro do JS. Passe data-debug na tag pra logar o
 * que foi encontrado e aplicado.
 */
(function () {
  const script = document.currentScript;
  const stylesheetUrl = script?.dataset.stylesheet;
  if (!stylesheetUrl) return;

  const DEBUG = script.dataset.debug !== undefined;
  // O app usa <shopify-forms-embed>; o prefixo cobre qualquer outro custom element
  // que ele venha a inserir.
  const HOST_SELECTOR = "shopify-forms-embed";
  const HOST_PREFIX = "shopify-forms";
  const MARKER = "data-theme-forms-styles";
  // <style> do snippets/font-faces.liquid, lido em runtime pra os @font-face do tema
  // entrarem junto — sem repetir as declarações nem a lista de fontes aqui.
  const FONT_FACES_ID = "theme-font-faces";
  // attachShadow não gera mutação: depois de ver o host, só resta olhar frame a
  // frame até o shadow root existir (~10s a 60fps).
  const SHADOW_ROOT_MAX_FRAMES = 600;

  const styled = new WeakSet();
  let stylesRequest = null;

  const log = (...args) => {
    if (DEBUG) console.log("[shopify-forms-styles]", ...args);
  };

  const isHost = (node) =>
    node.nodeType === 1 && node.localName.startsWith(HOST_PREFIX);

  const fontFaces = () => {
    const fonts = document.getElementById(FONT_FACES_ID);

    if (!fonts) {
      log(`#${FONT_FACES_ID} não encontrado: as fontes do tema não vão junto`);
      return "";
    }

    return `${fonts.textContent}\n`;
  };

  // Buscado uma vez e adotado por todos os hosts — um CSSStyleSheet pode ser
  // compartilhado entre shadow roots.
  const loadStyles = () => {
    stylesRequest =
      stylesRequest ||
      fetch(stylesheetUrl)
        .then((response) => (response.ok ? response.text() : ""))
        .then((text) => {
          if (!text) return null;

          const css = fontFaces() + text;

          try {
            const sheet = new CSSStyleSheet();
            sheet.replaceSync(css);
            return { sheet: sheet };
          } catch (error) {
            // Safari < 16.4 não constrói CSSStyleSheet; replaceSync também recusa
            // @import.
            log("CSSStyleSheet recusado, usando <style>", error);
            return { css: css };
          }
        })
        .catch((error) => {
          log("falha ao buscar o CSS", error);
          return null;
        });

    return stylesRequest;
  };

  const applyStyles = (host) => {
    const shadowRoot = host.shadowRoot;
    if (!shadowRoot || styled.has(host)) return;

    styled.add(host);

    loadStyles().then((styles) => {
      if (!styles) return;

      // Folhas adotadas entram depois do CSS do próprio app na ordem da cascata,
      // então ganham em empate de especificidade.
      if (styles.sheet && "adoptedStyleSheets" in shadowRoot) {
        if (!shadowRoot.adoptedStyleSheets.includes(styles.sheet)) {
          shadowRoot.adoptedStyleSheets = [
            ...shadowRoot.adoptedStyleSheets,
            styles.sheet,
          ];
        }

        log("folha adotada em", host.localName);
        return;
      }

      if (shadowRoot.querySelector(`style[${MARKER}]`)) return;

      const style = document.createElement("style");
      style.setAttribute(MARKER, "");
      style.textContent = styles.css || "";
      shadowRoot.appendChild(style);

      log("<style> injetado em", host.localName);
    });
  };

  const waitForShadowRoot = (host, frame) => {
    if (styled.has(host)) return;
    if (host.shadowRoot) return applyStyles(host);

    const next = (frame || 0) + 1;
    if (next > SHADOW_ROOT_MAX_FRAMES) {
      log("shadow root não apareceu em", host.localName);
      return;
    }

    requestAnimationFrame(() => waitForShadowRoot(host, next));
  };

  const track = (node) => {
    if (isHost(node)) {
      log("host encontrado", node.localName);
      waitForShadowRoot(node);
      return;
    }

    // O host pode chegar dentro de um wrapper (app embed block, por exemplo).
    node
      .querySelectorAll?.(HOST_SELECTOR)
      .forEach((host) => waitForShadowRoot(host));
  };

  document
    .querySelectorAll(HOST_SELECTOR)
    .forEach((host) => waitForShadowRoot(host));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) track(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  log("observando a inserção do embed");
})();
